/*
  Completes the category -> design submenus in the header's mobile menu and
  desktop "Menu" dropdown (snippets/site-header.liquid).

  The Liquid there builds each list by looping collection.products, which
  Shopify caps at a collection's first 50 products outside {% paginate %}.
  A large category silently loses every design past that point - confirmed
  live: Cotton Sarees has 164 sarees in 24 designs, but the menu listed 8.

  When a submenu is opened, this fetches the whole collection from
  /collections/<handle>/products.json (250 per request, the endpoint's own
  maximum) and rebuilds the list with exactly the same rules as the Liquid:
  available products only, one entry per "<title before |>" + "design:" tag
  pair, in collection order, labelled the way snippets/design-label.liquid
  renders it, linking to the same title:/tag: search. The server-rendered
  first-50 list stays as-is until then, and is kept if the fetch fails.

  Results are cached in sessionStorage so each category is fetched at most
  once per tab session - the storefront rate-limits bursts of these requests.
*/
(function () {
  var CACHE_PREFIX = 'tws_menu_designs:';
  var CACHE_TTL_MS = 10 * 60 * 1000;
  var PAGE_LIMIT = 250;
  var MAX_PAGES = 8;
  var inFlight = {};

  function readCache(url) {
    try {
      var cached = JSON.parse(sessionStorage.getItem(CACHE_PREFIX + url));
      if (cached && Date.now() - cached.time < CACHE_TTL_MS) return cached.entries;
    } catch (e) {}
    return null;
  }

  function writeCache(url, entries) {
    try {
      sessionStorage.setItem(CACHE_PREFIX + url, JSON.stringify({ time: Date.now(), entries: entries }));
    } catch (e) {}
  }

  // Liquid's `capitalize`: first character upper-cased, the rest lower-cased.
  function capitalize(word) {
    return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
  }

  // Mirrors snippets/design-label.liquid as it actually renders in the menu:
  // the slug after the last ":", hyphens to spaces, split on whitespace
  // (Liquid's split: ' ' drops empty pieces, so "vol--2" becomes "Vol2"),
  // each word capitalized, joined with no separator ("PeparMirrorVol2").
  function designLabel(designTag) {
    var value = designTag.split(':').pop().replace(/-/g, ' ');
    return value.split(/\s+/).filter(Boolean).map(capitalize).join('');
  }

  function productTags(product) {
    if (Array.isArray(product.tags)) return product.tags;
    return String(product.tags || '').split(',').map(function (tag) { return tag.trim(); });
  }

  function entriesFromProducts(products) {
    var seen = {};
    var entries = [];
    products.forEach(function (product) {
      // Same rule as snippets/product-in-stock.liquid: the "Bulk (N+ qty)"
      // tiers are untracked and always available, so only a non-Bulk
      // variant's availability counts.
      var variants = product.variants || [];
      var regularVariants = variants.filter(function (variant) { return String(variant.title || '').indexOf('Bulk (') === -1; });
      var available = (regularVariants.length ? regularVariants : variants).some(function (variant) { return variant.available; });
      if (!available) return;
      var base = String(product.title || '').split('|')[0].trim();
      var designTag = '';
      productTags(product).some(function (tag) {
        if (tag.toLowerCase().slice(0, 7) === 'design:') {
          designTag = tag;
          return true;
        }
        return false;
      });
      var label = designTag ? base + ' - ' + designLabel(designTag) : base;
      var key = label + '@@' + base + '@@' + designTag;
      if (seen[key]) return;
      seen[key] = true;
      entries.push({ label: label, base: base, tag: designTag });
    });
    return entries;
  }

  function fetchAllProducts(url) {
    var products = [];
    function fetchPage(page) {
      return fetch(url + '/products.json?limit=' + PAGE_LIMIT + '&page=' + page, { headers: { Accept: 'application/json' } })
        .then(function (response) {
          if (!response.ok) throw new Error('HTTP ' + response.status);
          return response.json();
        })
        .then(function (data) {
          var batch = (data && data.products) || [];
          products = products.concat(batch);
          if (batch.length === PAGE_LIMIT && page < MAX_PAGES) return fetchPage(page + 1);
          return products;
        });
    }
    return fetchPage(1);
  }

  function loadEntries(url) {
    var cached = readCache(url);
    if (cached) return Promise.resolve(cached);
    if (!inFlight[url]) {
      inFlight[url] = fetchAllProducts(url)
        .then(function (products) {
          var entries = entriesFromProducts(products);
          writeCache(url, entries);
          return entries;
        })
        .finally(function () {
          delete inFlight[url];
        });
    }
    return inFlight[url];
  }

  function searchHref(entry) {
    var query = 'title:"' + entry.base + '"';
    if (entry.tag) query += ' tag:"' + entry.tag + '"';
    var searchUrl = (window.routes && window.routes.searchUrl) || '/search';
    return searchUrl + '?q=' + encodeURIComponent(query).replace(/%20/g, '+') + '&type=product';
  }

  function render(list, entries) {
    var existingLinks = list.querySelectorAll('a');
    // Only ever adds designs - never shrinks a list the server already
    // rendered (e.g. a stale cache entry from before new stock arrived).
    if (entries.length <= existingLinks.length) return;
    var linkClass = existingLinks.length ? existingLinks[0].className : '';
    var fragment = document.createDocumentFragment();
    entries.forEach(function (entry) {
      var item = document.createElement('li');
      var link = document.createElement('a');
      link.href = searchHref(entry);
      link.className = linkClass;
      link.textContent = entry.label;
      item.appendChild(link);
      fragment.appendChild(item);
    });
    list.innerHTML = '';
    list.appendChild(fragment);
  }

  function complete(list) {
    if (list.hasAttribute('data-menu-designs-complete')) return;
    list.setAttribute('data-menu-designs-complete', '');
    loadEntries(list.getAttribute('data-menu-designs-url'))
      .then(function (entries) {
        render(list, entries);
      })
      .catch(function () {
        // Keep the server-rendered list; allow a retry on the next open.
        list.removeAttribute('data-menu-designs-complete');
      });
  }

  document.addEventListener('click', function (event) {
    var toggle = event.target.closest && event.target.closest('[data-action="toggle-collapsible"][aria-controls]');
    if (!toggle) return;
    var list = document.getElementById(toggle.getAttribute('aria-controls'));
    if (list && list.hasAttribute('data-menu-designs-url')) complete(list);
  });

  // The search dropdown's Collections section (sections/predictive-search.
  // liquid) lists each collection's designs the same way, already visible -
  // no toggle to click - so complete those as soon as theme.js renders them.
  var searchResults = document.querySelector('.search-bar__results-inner');
  if (searchResults && window.MutationObserver) {
    new MutationObserver(function () {
      var lists = searchResults.querySelectorAll('.search-bar__collection-designs[data-menu-designs-url]:not([data-menu-designs-complete])');
      for (var i = 0; i < lists.length; i++) complete(lists[i]);
    }).observe(searchResults, { childList: true, subtree: true });
  }
})();
